import React, { useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Bug, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";

/**
 * "Report Bug" button + modal. Lets logged-in users send a bug report
 * straight to the Discord #bug-reports channel via the backend webhook.
 *
 * Screenshot is OPTIONAL — when present, it is uploaded through the existing
 * /uploads/image endpoint and the resulting URL is embedded in the Discord
 * post.
 */
export default function BugReportButton({ variant = "ghost", className = "" }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [screenshotUrl, setScreenshotUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInput = useRef(null);

  const reset = () => {
    setTitle("");
    setDescription("");
    setScreenshotUrl("");
    setUploading(false);
    setSubmitting(false);
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Only image files are supported for screenshots.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Screenshot must be under 10MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setScreenshotUrl(data.url || "");
      toast.success("Screenshot attached.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Screenshot upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (title.trim().length < 3) { toast.error("Title must be at least 3 characters."); return; }
    if (description.trim().length < 10) { toast.error("Please describe the bug (10+ characters)."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bugs/report", {
        title: title.trim(),
        description: description.trim(),
        screenshot_url: screenshotUrl || null,
      });
      if (data.webhook_ok) {
        toast.success("Bug report submitted — thank you!");
      } else {
        // Stored but not delivered to Discord — surface the error softly.
        toast.success("Bug report saved. Discord delivery failed: " + (data.webhook_error || "see logs"));
      }
      setOpen(false);
      reset();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to submit bug report.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button
          variant={variant}
          className={"text-rose-300 hover:bg-rose-950/30 " + className}
          data-testid="bug-report-open-btn"
        >
          <Bug className="w-4 h-4 mr-2" />
          Report Bug
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-950 border border-rose-500/30 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-white">
            <Bug className="w-5 h-5 text-amber-400" /> Report a Bug
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Help us improve the site. Reports go straight to our team on Discord.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-wider">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="Short summary"
              className="mt-1.5 bg-black/40 border-white/10 text-white"
              data-testid="bug-title-input"
            />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-wider">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={4000}
              rows={6}
              placeholder="Steps to reproduce, what you expected, what actually happened…"
              className="mt-1.5 bg-black/40 border-white/10 text-white"
              data-testid="bug-description-input"
            />
            <div className="text-xs text-zinc-500 mt-1">{description.length} / 4000</div>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-wider">Screenshot (optional)</Label>
            {screenshotUrl ? (
              <div className="mt-1.5 flex items-center gap-3 p-2 rounded-md border border-white/10 bg-black/40">
                <img src={screenshotUrl.startsWith("http") ? screenshotUrl : `${process.env.REACT_APP_BACKEND_URL}${screenshotUrl}`}
                     alt="screenshot preview" className="w-16 h-16 object-cover rounded" />
                <div className="text-xs text-zinc-300 flex-1 truncate">Attached</div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setScreenshotUrl("")}
                  className="text-rose-300 hover:bg-rose-950/30"
                  data-testid="bug-screenshot-remove-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="mt-1.5">
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="bug-screenshot-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInput.current?.click()}
                  disabled={uploading}
                  className="border-white/10 text-zinc-200 hover:bg-zinc-900"
                  data-testid="bug-screenshot-pick-btn"
                >
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Attach Screenshot
                </Button>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={submitting} data-testid="bug-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting || uploading}
              className="bg-rose-600 hover:bg-rose-500"
              data-testid="bug-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bug className="w-4 h-4 mr-2" />}
              Submit
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
